import type {
  AppField,
  EditorData,
  Filter,
  FormStyle,
  Language,
  Locale,
  ProgressBarPageNames,
  ProgressBarStyle,
  ProgressBarVariant,
  SortRule,
  Tag,
  Theme,
  Timezone,
} from "@smartsuite/types";
import type { FormikValues } from "formik";
import type { Country } from "react-phone-number-input";
import type { Fields } from "./fields";
import type { FileSecurity } from "./file";

export type { FormStyle, ProgressBarPageNames, ProgressBarStyle, ProgressBarVariant };

export interface FeatureFlags {
  form_checklist_field?: boolean;
  checklist_team_assignment?: boolean;
  /** The workspace is on the new date vocabulary; gates date evaluation, not a form feature. */
  date_filter_new_options?: boolean;
  form_appearance_style?: boolean;
  form_date_range?: boolean;
  form_footer_style?: boolean;
  form_header_style?: boolean;
  form_layout_style?: boolean;
  form_linked_record_table_display?: boolean;
  form_pages?: boolean;
  form_progress_bar?: boolean;
  form_select_display_types?: boolean;
  form_team_field?: boolean;
  form_view_create_linked_records?: boolean;
  form_yesno_default_value?: boolean;
  lr_default_value?: boolean;
  lr_originating_record_default?: boolean;
}

export type SubmissionPageMode = "message" | "redirect";

export interface SubmissionPageConfig {
  mode: SubmissionPageMode;
  display_icon: boolean;
  title: string;
  description: string | EditorData | null;
  redirect_url: string | null;
}

export interface FormPage {
  id: string;
  name: string;
  items: FormConfigItem[];
  page_type?: string;
  review?: {
    title?: string | null;
    description?: string | EditorData | null;
    field_slugs?: string[];
  } | null;
  submission?: SubmissionPageConfig | null;
}

export interface FormConfig {
  application: FormConfigApplication;
  current_user?: CurrentUser;
  feature_flags?: FeatureFlags;
  file_security?: FileSecurity;
  form_state: FormState;
  sharing_hash: string;
  display_smartsuite_branding: boolean;
  has_active_paid_subscription?: boolean;
  filestack_api_key: string;
  tags: Tag[];
  solution_color?: string | null;
}

export interface Member {
  id: string;
  name: string;
}

export interface Team {
  id: string;
  name: string;
  color: Array<{ value: string; name: string }>;
}

export interface CurrentUser {
  id: string;
  full_name: {
    first_name: string;
    last_name: string;
    sys_root: string;
  };
  email: string;
  profile_image: string | null;
  timezone: Timezone;
  language: Language;
  locale: Locale;
  theme: Theme;
}

export interface FormState {
  description: string | null;
  display_message: string | null;
  has_progress_restore: boolean;
  items: FormConfigItem[];
  logo_handle: string | null;
  redirect_to_url: string | null;
  submit_label: string | null;
  time_format: "12h" | "24h" | null;
  title: string | null;
  logo_href?: string;
  tab_title?: string | null;
  pages?: FormPage[];
  style?: FormStyle | null;
}

export type FormStateItemConditions = {
  enabled?: boolean;
  object?: Filter;
};

export type FormStateItemSelections = {
  enabled?: boolean;
  choiceIDs?: string[];
};

export interface FormConfigItem {
  type: FormItemType;
  slug: string;
  required: boolean;
  params: {
    caption?: string;
    conditions?: FormStateItemConditions;
    linked_record_conditions?: FormStateItemConditions;
    items?: FormConfigItem[];
    label?: string;
    selections?: FormStateItemSelections;
    hidden?: boolean;
    read_only?: boolean;
    cover_image_field?: string;
    helpText?: {
      enabled: boolean;
      value: EditorData;
    };
    default_value_enabled?: boolean;
    default_value?: unknown;
    display_type?: "standard" | "table";
    visible_fields?: string[];
    table_visible_fields?: string[];
    /**
     * Checklist Options, set per form item. `due_date_enabled` / `assignee_enabled` have no
     * field-level counterpart; the other two also exist on the field and the value here
     * overrides it for this form only. Absent means "fall back" — to the field's value where
     * there is one, otherwise to the option's default.
     */
    due_date_enabled?: boolean;
    assignee_enabled?: boolean;
    new_entries_allowed?: boolean;
    modify_defaults_allowed?: boolean;
    [key: string]: any;
  };
}

export type ImageAlignment = "left" | "right" | "center" | "cover";

export interface FormConfigImageItem extends FormConfigItem {
  params: {
    caption?: string;
    conditions?: FormStateItemConditions;
    items?: FormConfigItem[];
    label?: string;
    selections?: FormStateItemSelections;
    imageUrl?: string;
    fileStackImageHandle?: string;
    alignment?: ImageAlignment;
    maxHeight?: number;
  };
}

export interface FormConfigVideoItem extends FormConfigItem {
  params: {
    caption?: string;
    conditions?: FormStateItemConditions;
    items?: FormConfigItem[];
    label?: string;
    selections?: FormStateItemSelections;
    videoUrl?: string;
    maxHeight?: number;
  };
}

export type CalloutType = "info" | "note" | "success" | "warning" | "error" | "decision";

export interface FormConfigCalloutItem extends FormConfigItem {
  params: {
    caption?: string;
    conditions?: FormStateItemConditions;
    items?: FormConfigItem[];
    label?: string;
    selections?: FormStateItemSelections;
    calloutType?: CalloutType;
    doc?: string;
  };
}

interface Color {
  color: string;
}

export interface FormConfigDividerItem extends FormConfigItem {
  params: {
    caption?: string;
    conditions?: FormStateItemConditions;
    items?: FormConfigItem[];
    label?: string;
    selections?: FormStateItemSelections;
    title?: string;
    color?: Color;
  };
}

export enum FormItemType {
  heading = "heading",
  field = "field",
  html_block = "html_block",
  section = "section",
  image = "image",
  video = "video",
  callout = "callout",
  divider = "divider",
  recaptcha = "recaptcha",
  consent = "consent",
  pdf_viewer = "pdf_viewer",
}

export interface FormConfigApplication {
  structure: FormConfigApplicationStructureItem[];
}

export interface FormConfigApplicationStructureItem {
  slug: string;
  label: string;
  field_type: Fields;
  params: AppFieldParams;
}

export interface FieldChoice {
  label: string;
  value: string;
}

export interface AppFieldParams {
  choices?: FieldChoice[];
  default_country?: Country;
  entries_allowed?: "single" | "multiple";
  help_text?: string;
  linked_application?: string;
  max_length?: number;
  max_value?: number;
  min_value?: number;
  placeholder?: string;
  required?: boolean;
  scale?: number;
  display_fields?: string[];
  color?: string;
  display_format?: string;
  duration_format?: string;
  include_labels?: boolean;
  rating_highest?: string;
  rating_lowest?: string;
  filter_data?: Filter;
  sort_data?: SortRule[];
  symmetric_field?: string;
  autocreated_symmetric_field?: boolean;
  do_not_create_backlink?: boolean;
  select_record_modal_sort_data?: SortRule[];
  select_record_modal_visible_fields?: string[];
  visible_fields?: string[];
  read_only?: boolean;
  field_selection?: string;
  target_field_structure?: AppField;
  linked_field?: string;
  source_field_slug?: string;
  include_filters?: boolean;
  field_filter?: Filter;
  allow_negative?: boolean;
  allow_zero?: boolean;
  value_increment?: number;
  separator?: boolean;
  decimal_separator?: string;
  thousand_separator?: string;
  currency?: string;
}

export interface BaseRecord {
  [key: string]: any;
}

export type FormValues = FormikValues;

export interface LinkedRecordObject {
  id: string;
  title: string;
}
